<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { ref, watch, inject } from "vue"
import { useI18n } from "vue-i18n"
import { watchDebounced } from "@vueuse/core"
import type { Services } from "@services"
import type { UsageProfile } from "@modules/usage-profile/types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            heading1: "Associer un profil existant à {name}",
            search_placeholder: "Rechercher un profil…",
            new: "Créer un nouveau profil",
            submit: "Associer",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Identifiant du bâtiment à associer */
        buildingId: string
        /** Identifiant de l'entreprise */
        companyId: string
        /** Nom du bâtiment */
        buildingName?: string
        /** Profil présélectionné par défaut */
        profileId?: string
    }>(),
    {
        buildingName: "",
        profileId: "",
    }
)

const emit = defineEmits<{
    /** Terminé avec succès */
    done: [payload: { profile: UsageProfile }]
    /** Clic sur "Créer un nouveau profil" */
    "click:new": []
}>()

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")!

// ================================
// STATE
// ================================
/** Terme de recherche */
const search = ref("")
/** Liste des profils chargée */
const profiles = ref<UsageProfile[]>([])
/** Message d’erreur */
const error = ref<string | null>(null)
/** Chargement en cours lors de l’association */
const loading = ref(false)
/** Chargement en cours (fetch) */
const pending = ref(false)
/** Profil sélectionné (radio) */
const profileSelected = ref<string | null>(props.profileId || null)

// ================================
// BUSINESS LOGIC (fetch)
// ================================
/** Charge la liste des profils (avec recherche) */
async function fetchProfiles(): Promise<void> {
    try {
        pending.value = true
        error.value = null
        profiles.value = await services.$getUsageProfiles({
            noPage: true,
            search: search.value,
        })
    } catch (err) {
        error.value = (err as Error).message
    } finally {
        pending.value = false
    }
}

// ================================
// INTERACTIONS
// ================================
/**
 * Soumission : associe le profil sélectionné au bâtiment puis émet done
 */
async function onClickSubmit() {
    if (!profileSelected.value || loading.value) return
    try {
        loading.value = true
        error.value = null
        const profile = await services.$addUsageProfileToBuilding({
            profileId: profileSelected.value,
            buildingIds: [props.buildingId],
        })
        emit("done", { profile })
    } catch (err) {
        error.value = (err as Error).message
    } finally {
        loading.value = false
    }
}

// ================================
// WATCHERS
// ================================
watchDebounced(
    search,
    () => {
        fetchProfiles()
    },
    { debounce: 500, maxWait: 1000 }
)

watch(
    () => props.profileId,
    (id) => {
        profileSelected.value = id || null
    }
)

// Chargement initial
fetchProfiles()
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div class="usage-profile-exist flex flex-col gap-12">
        <h2 class="text-xl font-semibold">
            {{
                t("heading1", {
                    name: buildingName || "le bâtiment",
                })
            }}
        </h2>

        <BaseInputText
            v-if="profiles.length > 0 || !!search"
            v-model="search"
            class="w-full max-w-card-mobile mx-auto pt-4"
            :disabled="pending"
            :placeholder="t('search_placeholder')"
        />

        <div v-if="pending" class="flex flex-col gap-2 min-h-80">
            <BaseSkeleton
                v-for="i in 7"
                :key="i"
                height="2.8rem"
                width="100%"
            />
        </div>
        <UsageProfileList
            v-else
            class="usage-profile-exist__list"
            :profiles="profiles"
            :search="search"
            selectable
            @click:profile="profileSelected = $event"
        />

        <div class="flex flex-wrap justify-center gap-4">
            <BaseButton
                outline
                severity="secondary"
                :disabled="loading"
                @click="$emit('click:new')"
            >
                {{ t("new") }}
            </BaseButton>
            <BaseButton
                :disabled="!profileSelected"
                :loading="loading"
                @click="onClickSubmit"
            >
                {{ t("submit") }}
            </BaseButton>
        </div>

        <BaseAppMessage v-if="error" severity="error" :message="error" />
    </div>
</template>
